
import React, { useState } from 'react';
import { getVolumes, setVolumes } from '../../services/audioService';
import { playUiSfx } from '../../utils/uiAudio';
import {
  hudPreferencesStore,
  useHudPreferences,
  type HudPreset,
  type HudPreferences,
} from '../../stores/hudPreferences';
import { ModalShell, PanelHeader, buttons, type, surfaces, cn } from './kit';
import PlayerStatsPanel from './PlayerStatsPanel';

type DashboardTab = 'hud' | 'audio' | 'career' | 'system';

interface GameSettingsDashboardProps {
  onClose: () => void;
  onExitToMenu: () => void;
  onSaveNow?: () => void;
  showPerformanceMonitor?: boolean;
  onTogglePerformanceMonitor?: () => void;
  onOpenCommandDashboard?: () => void;
  onOpenSOC?: () => void;
}

const HUD_TOGGLES: { key: keyof HudPreferences; label: string; description: string }[] = [
  { key: 'showAiPanel', label: 'AI Risk Officer', description: 'Goal panel top-right' },
  { key: 'showObjectives', label: 'Objectives', description: 'Mission milestones sidebar' },
  { key: 'showNewsFeed', label: 'News Feed', description: 'Ticker bottom-right' },
  { key: 'showLayerPanel', label: 'Layer View Panel', description: 'Full underground controls' },
  { key: 'showOrgShortcuts', label: 'Org Shortcuts', description: 'HR / TPRM / Audit in stats bar' },
  { key: 'showQuickDebtButton', label: 'Quick Repay', description: 'Floating loan repay button' },
];

const GameSettingsDashboard: React.FC<GameSettingsDashboardProps> = ({
  onClose,
  onExitToMenu,
  onSaveNow,
  showPerformanceMonitor = false,
  onTogglePerformanceMonitor,
  onOpenCommandDashboard,
  onOpenSOC,
}) => {
  const hud = useHudPreferences();
  const [tab, setTab] = useState<DashboardTab>('hud');
  const [master, setMaster] = useState(() => getVolumes().master);
  const [sfx, setSfx] = useState(() => getVolumes().sfx);
  const [ambient, setAmbient] = useState(() => getVolumes().ambient);

  const handleVolumeChange = (type: 'master' | 'sfx' | 'ambient', val: number) => {
    const nextMaster = type === 'master' ? val : master;
    const nextSfx = type === 'sfx' ? val : sfx;
    const nextAmbient = type === 'ambient' ? val : ambient;
    if (type === 'master') setMaster(val);
    if (type === 'sfx') setSfx(val);
    if (type === 'ambient') setAmbient(val);
    setVolumes(nextMaster, nextSfx, nextAmbient);
  };

  const applyPreset = (preset: HudPreset) => {
    playUiSfx('click');
    hudPreferencesStore.applyPreset(preset);
  };

  const toggleHud = (key: keyof HudPreferences) => {
    playUiSfx('click');
    hudPreferencesStore.setPreference(key, !hud[key]);
  };

  return (
    <ModalShell
      variant="slideRight"
      zLevel="menu"
      testId="game-settings-dashboard"
      overlayClassName={surfaces.overlayLight}
    >
      <div className="h-full bg-slate-950 border-l border-slate-700 shadow-2xl flex flex-col overflow-hidden">
        <PanelHeader
          title="Command Menu"
          subtitle="HUD · Audio · Career · System"
          onClose={() => { playUiSfx('click'); onClose(); }}
          closeLabel="Close menu"
        />

        <nav className="flex border-b border-slate-800 shrink-0">
          {(['hud', 'audio', 'career', 'system'] as const).map((t) => (
            <button
              key={t}
              type="button"
              data-testid={t === 'career' ? 'settings-career-tab' : undefined}
              onClick={() => { playUiSfx('click'); setTab(t); }}
              className={cn(buttons.tab, tab === t ? buttons.tabActive : buttons.tabIdle)}
            >
              {t === 'hud' ? 'HUD Layout' : t === 'audio' ? 'Audio' : t === 'career' ? 'Career' : 'System'}
            </button>
          ))}
        </nav>

        <div className="flex-1 overflow-y-auto overscroll-contain p-5">
          {tab === 'hud' && (
            <div className="space-y-6">
              <section>
                <h3 className={cn(type.eyebrow, 'mb-3')}>Presets</h3>
                <div className="grid grid-cols-3 gap-2">
                  {(['minimal', 'balanced', 'full'] as HudPreset[]).map((preset) => {
                    const active = hud.activePreset === preset;
                    return (
                      <button
                        key={preset}
                        type="button"
                        data-testid={`hud-preset-${preset}`}
                        onClick={() => applyPreset(preset)}
                        className={cn(
                          'py-2 px-2 rounded-lg border text-[10px] font-bold uppercase transition-colors',
                          active
                            ? 'border-cyan-400 bg-cyan-900/40 text-cyan-100 shadow-[0_0_12px_rgba(6,182,212,0.25)]'
                            : 'border-slate-700 bg-slate-900 text-slate-300 hover:border-cyan-500/50 hover:text-white'
                        )}
                      >
                        {preset}
                      </button>
                    );
                  })}
                </div>
                <p className={cn(type.caption, 'text-[10px] text-slate-600 mt-2')}>
                  Default is <span className="text-slate-400">minimal</span> — stats, layer strip, build toolbar, and ☰ Menu only.
                  Turn panels on here when you need them.
                </p>
              </section>

              <section>
                <h3 className={cn(type.eyebrow, 'mb-3')}>Panels</h3>
                <div className="space-y-2">
                  {HUD_TOGGLES.map(({ key, label, description }) => (
                    <label
                      key={key}
                      className="flex items-center justify-between gap-3 p-3 rounded-lg border border-slate-800 bg-slate-900/60 cursor-pointer hover:border-slate-600 transition-colors"
                    >
                      <div>
                        <div className={cn(type.body, 'font-medium text-white')}>{label}</div>
                        <div className={cn(type.eyebrow, 'normal-case tracking-normal text-slate-500')}>{description}</div>
                      </div>
                      <input
                        type="checkbox"
                        checked={hud[key]}
                        onChange={() => toggleHud(key)}
                        className="w-4 h-4 accent-cyan-500 shrink-0"
                      />
                    </label>
                  ))}
                </div>
              </section>

              <section>
                <h3 className={cn(type.eyebrow, 'mb-3')}>Quick Open</h3>
                <div className="grid grid-cols-2 gap-2">
                  {onOpenCommandDashboard && (
                    <button
                      type="button"
                      onClick={() => { playUiSfx('click'); onOpenCommandDashboard(); onClose(); }}
                      className="py-3 rounded-lg bg-indigo-900/50 border border-indigo-700 text-indigo-200 text-[10px] font-bold uppercase hover:bg-indigo-800/50"
                    >
                      📊 Dashboard (R)
                    </button>
                  )}
                  {onOpenSOC && (
                    <button
                      type="button"
                      onClick={() => { playUiSfx('click'); onOpenSOC(); onClose(); }}
                      className="py-3 rounded-lg bg-red-900/40 border border-red-800 text-red-200 text-[10px] font-bold uppercase hover:bg-red-900/60"
                    >
                      🚨 SOC (E)
                    </button>
                  )}
                </div>
              </section>
            </div>
          )}

          {tab === 'audio' && (
            <div className="space-y-6">
              {(['master', 'sfx', 'ambient'] as const).map((volType) => {
                const val = volType === 'master' ? master : volType === 'sfx' ? sfx : ambient;
                const label = volType === 'master' ? 'Master Volume' : volType === 'sfx' ? 'Sound Effects' : 'Ambient City Noise';
                return (
                  <div key={volType}>
                    <div className={cn('flex justify-between uppercase font-bold mb-2', type.caption)}>
                      <span>{label}</span>
                      <span>{Math.round(val * 100)}%</span>
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.05"
                      value={val}
                      onChange={(e) => handleVolumeChange(volType, parseFloat(e.target.value))}
                      className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-cyan-500"
                    />
                  </div>
                );
              })}
            </div>
          )}

          {tab === 'career' && (
            <PlayerStatsPanel />
          )}

          {tab === 'system' && (
            <div className="space-y-6">
              <div>
                <h3 className={cn(type.eyebrow, 'mb-2')}>Shortcuts</h3>
                <div className={cn('grid grid-cols-2 gap-2 text-[10px] font-mono', type.caption)}>
                  <div><span className="text-white bg-slate-700 px-1 rounded">ESC</span> Close / Deselect</div>
                  <div><span className="text-white bg-slate-700 px-1 rounded">B</span> Bulldoze</div>
                  <div><span className="text-white bg-slate-700 px-1 rounded">R</span> Dashboard</div>
                  <div><span className="text-white bg-slate-700 px-1 rounded">E</span> SOC</div>
                  <div><span className="text-white bg-slate-700 px-1 rounded">P</span> Policies</div>
                  <div><span className="text-white bg-slate-700 px-1 rounded">Space</span> Pause</div>
                </div>
              </div>

              {onTogglePerformanceMonitor && (
                <button
                  type="button"
                  onClick={onTogglePerformanceMonitor}
                  className={cn(
                    'w-full py-2 rounded-lg border font-bold text-xs uppercase tracking-widest',
                    showPerformanceMonitor
                      ? 'bg-cyan-600 border-cyan-500 text-white'
                      : 'bg-slate-800 border-slate-600 text-slate-300'
                  )}
                >
                  Performance Monitor: {showPerformanceMonitor ? 'ON' : 'OFF'}
                </button>
              )}

              {onSaveNow && (
                <button
                  type="button"
                  data-testid="save-now"
                  onClick={onSaveNow}
                  className="w-full py-3 bg-emerald-800 hover:bg-emerald-700 border border-emerald-600 text-emerald-100 font-bold rounded uppercase text-xs tracking-widest"
                >
                  Save Sandbox Now
                </button>
              )}

              <button
                type="button"
                onClick={() => {
                  if (window.confirm('Exit to menu? Sandbox progress auto-saves on exit.')) {
                    onExitToMenu();
                  }
                }}
                className="w-full py-3 bg-red-900/30 hover:bg-red-900/50 border border-red-900 text-red-400 font-bold rounded uppercase text-xs tracking-widest"
              >
                Exit to Menu
              </button>
            </div>
          )}
        </div>

        <footer className="p-4 border-t border-slate-800 shrink-0">
          <button
            type="button"
            onClick={() => { playUiSfx('click'); onClose(); }}
            className={cn(buttons.primary, 'w-full')}
          >
            Resume Game
          </button>
        </footer>
      </div>
    </ModalShell>
  );
};

export default GameSettingsDashboard;